import { useEffect, useRef } from "react";
import { HERO_CURSOR_MODE } from "../config/heroEffects";

const LERP = 0.12;
const MAX_PUPIL = 6;
const MAX_HEAD_TILT = 12;

const lerp = (a, b, t) => a + (b - a) * t;

const HeroCursorFace = () => {
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const leftPupilRef = useRef(null);
  const rightPupilRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const hero = root?.closest(".hero-v2");
    if (!root || !hero) return undefined;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion) return undefined;

    const eyes = [
      { el: leftPupilRef.current, cx: 0, cy: 0 },
      { el: rightPupilRef.current, cx: 0, cy: 0 },
    ];

    const state = {
      active: false,
      mx: 0,
      my: 0,
      headX: 0,
      headY: 0,
      pupils: eyes.map(() => ({ x: 0, y: 0 })),
    };

    let rafId = null;

    const measureEyes = () => {
      eyes.forEach((eye, i) => {
        if (!eye.el) return;
        const socket = eye.el.closest(".hero-cursor-face__eye");
        const rect = socket?.getBoundingClientRect();
        if (!rect) return;
        eye.cx = rect.left + rect.width / 2;
        eye.cy = rect.top + rect.height / 2;
        state.pupils[i] = state.pupils[i] || { x: 0, y: 0 };
      });
    };

    const setPointer = (clientX, clientY) => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      state.mx = clientX;
      state.my = clientY;
      state.active = true;
      root.classList.add("hero-cursor-face--active");
    };

    const clearPointer = () => {
      state.active = false;
      root.classList.remove("hero-cursor-face--active");
    };

    const update = () => {
      if (headRef.current) {
        const headRect = headRef.current.getBoundingClientRect();
        const hx = headRect.left + headRect.width / 2;
        const hy = headRect.top + headRect.height / 2;

        if (state.active) {
          const dx = state.mx - hx;
          const dy = state.my - hy;
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          const dist = Math.min(Math.hypot(dx, dy) / 40, 1);
          state.headX = lerp(
            state.headX,
            Math.max(-MAX_HEAD_TILT, Math.min(MAX_HEAD_TILT, angle * 0.2 * dist)),
            LERP
          );
          state.headY = lerp(
            state.headY,
            Math.max(-7, Math.min(7, (dy / Math.max(headRect.height, 1)) * 16)),
            LERP
          );
        } else {
          state.headX = lerp(state.headX, 0, LERP);
          state.headY = lerp(state.headY, 0, LERP);
        }

        headRef.current.style.transform = `rotate(${state.headX}deg) translateY(${state.headY}px)`;
      }

      eyes.forEach((eye, i) => {
        if (!eye.el) return;
        const pupil = state.pupils[i];
        if (state.active) {
          const dx = state.mx - eye.cx;
          const dy = state.my - eye.cy;
          const dist = Math.hypot(dx, dy) || 1;
          const scale = Math.min(dist / 120, 1);
          const tx = (dx / dist) * MAX_PUPIL * scale;
          const ty = (dy / dist) * MAX_PUPIL * scale;
          pupil.x = lerp(pupil.x, tx, LERP * 1.4);
          pupil.y = lerp(pupil.y, ty, LERP * 1.4);
        } else {
          pupil.x = lerp(pupil.x, 0, LERP);
          pupil.y = lerp(pupil.y, 0, LERP);
        }
        eye.el.style.transform = `translate(${pupil.x}px, ${pupil.y}px)`;
      });

      rafId = window.requestAnimationFrame(update);
    };

    const onResize = () => measureEyes();
    const onMouseMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      if (touch) setPointer(touch.clientX, touch.clientY);
    };

    measureEyes();
    update();

    window.addEventListener("resize", onResize);
    hero.addEventListener("mousemove", onMouseMove, { passive: true });
    hero.addEventListener("mouseleave", clearPointer);
    hero.addEventListener("touchmove", onTouchMove, { passive: true });
    hero.addEventListener("touchend", clearPointer);

    return () => {
      window.removeEventListener("resize", onResize);
      hero.removeEventListener("mousemove", onMouseMove);
      hero.removeEventListener("mouseleave", clearPointer);
      hero.removeEventListener("touchmove", onTouchMove);
      hero.removeEventListener("touchend", clearPointer);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      root.classList.remove("hero-cursor-face--active");
    };
  }, []);

  const blendClass =
    HERO_CURSOR_MODE === "both" ? " hero-cursor-face--blend" : "";

  return (
    <div
      ref={rootRef}
      className={`hero-cursor-face${blendClass}`}
      aria-hidden="true"
    >
      <span className="hero-cursor-face__aura" />
      <span className="hero-cursor-face__orbit" />

      <div ref={headRef} className="hero-cursor-face__head">
        <span className="hero-cursor-face__rim" />
        <span className="hero-cursor-face__shine" />

        <span className="hero-cursor-face__brow hero-cursor-face__brow--left" />
        <span className="hero-cursor-face__brow hero-cursor-face__brow--right" />

        <div className="hero-cursor-face__eye hero-cursor-face__eye--left">
          <span className="hero-cursor-face__iris">
            <span ref={leftPupilRef} className="hero-cursor-face__pupil" />
            <span className="hero-cursor-face__catchlight" />
          </span>
        </div>

        <div className="hero-cursor-face__eye hero-cursor-face__eye--right">
          <span className="hero-cursor-face__iris">
            <span ref={rightPupilRef} className="hero-cursor-face__pupil" />
            <span className="hero-cursor-face__catchlight" />
          </span>
        </div>

        <span className="hero-cursor-face__cheek hero-cursor-face__cheek--left" />
        <span className="hero-cursor-face__cheek hero-cursor-face__cheek--right" />
        <span className="hero-cursor-face__smile" />
        <span className="hero-cursor-face__chin" />
      </div>
    </div>
  );
};

export default HeroCursorFace;
